import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, Eye, EyeOff, Save, X, Image as ImageIcon, Type, AlignLeft, Activity, Calendar, User, ExternalLink } from 'lucide-react';
import { portfolioApi, assetUrl, projectCategoryApi, sectionApi } from '../../../services/api';

interface PortfolioItem {
  id: string;
  title: string;
  description: string;
  category: string;
  image_url: string | null;
  slug: string;
  display_order: number;
  is_active: boolean;
  tags: string[];
  client: string;
  project_date: string;
  live_demo_url: string | null;
  github_url: string | null;
  gallery?: string[];
}

interface PortfolioFormState {
  title: string;
  description: string;
  category: string;
  image_url: string;
  display_order: number;
  is_active: boolean;
  tags: string[];
  client: string;
  project_date: string;
  live_demo_url: string;
  gallery: string[];
  disable_client: boolean;
  disable_project_date: boolean;
  disable_live_demo_url: boolean;
}

interface PortfolioIntroForm {
  heading: string;
  description: string;
  portfolio_page_heading: string;
  portfolio_page_description: string;
  layout_style: 'style4' | 'style5';
}

const normalizeLayoutStyle = (value: unknown): 'style4' | 'style5' => {
  const raw = String(value ?? '').trim().toLowerCase();
  if (raw === 'style4') return 'style4';
  if (raw === 'style5') return 'style5';
  return 'style4';
};

const toDateInputValue = (value: string | null | undefined): string => {
  if (!value) return '';
  const direct = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (direct) return direct[1];
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const ModernPortfolioEditor: React.FC = () => {
  const modalContentRef = useRef<HTMLDivElement | null>(null);
  const [categories, setCategories] = useState<{ id: number; name: string; display_order?: number; is_active?: boolean }[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<PortfolioItem | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [savingIntro, setSavingIntro] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [portfolioIntro, setPortfolioIntro] = useState<PortfolioIntroForm>({
    heading: 'Our Portfolio',
    description: '',
    portfolio_page_heading: 'Portfolio Projects',
    portfolio_page_description: 'Recent work and case studies',
    layout_style: 'style4'
  });
  const [formData, setFormData] = useState<PortfolioFormState>({
    title: '',
    description: '',
    category: '',
    image_url: '',
    display_order: 0,
    is_active: true,
    tags: [],
    client: '',
    project_date: '',
    live_demo_url: '',
    gallery: [],
    disable_client: false,
    disable_project_date: false,
    disable_live_demo_url: false
  });

  useEffect(() => {
    loadPortfolioItems();
    loadPortfolioIntro();
    loadCategories();
  }, []);

  useEffect(() => {
    if (!successMessage) return;
    const timer = window.setTimeout(() => setSuccessMessage(''), 3000);
    return () => window.clearTimeout(timer);
  }, [successMessage]);

  useEffect(() => {
    if (!errorMessage) return;
    const timer = window.setTimeout(() => setErrorMessage(''), 3500);
    return () => window.clearTimeout(timer);
  }, [errorMessage]);

  useEffect(() => {
    if (!showForm) return;
    window.requestAnimationFrame(() => {
      modalContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      modalContentRef.current?.focus();
    });
  }, [showForm, editingItem]);

  const loadPortfolioItems = async () => {
    try {
      setLoading(true);
      const response = await portfolioApi.getAll();
      setPortfolioItems(response.data || []);
    } catch (error) {
      console.error('Error loading portfolio items:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPortfolioIntro = async () => {
    try {
      const response = await sectionApi.get('portfolio');
      if (response?.success && response.data) {
        setPortfolioIntro({
          heading: String(response.data.heading || 'Our Portfolio'),
          description: String(response.data.description || ''),
          portfolio_page_heading: String(response.data.portfolio_page_heading || 'Portfolio Projects'),
          portfolio_page_description: String(response.data.portfolio_page_description || 'Recent work and case studies'),
          layout_style: normalizeLayoutStyle(response.data.layout_style)
        });
      }
    } catch (error) {
      console.error('Error loading portfolio heading settings:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await projectCategoryApi.getAll();
      if (response?.success && Array.isArray(response.data)) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error loading portfolio categories:', error);
    }
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      await projectCategoryApi.create({ name, display_order: categories.length + 1 });
      setNewCategoryName('');
      await loadCategories();
      setSuccessMessage('Portfolio category created successfully!');
      setErrorMessage('');
    } catch (error: any) {
      console.error('Error creating category:', error);
      setSuccessMessage('');
      setErrorMessage(error?.message || 'Failed to create category');
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await projectCategoryApi.delete(id);
      await loadCategories();
      setSuccessMessage('Portfolio category deleted successfully!');
      setErrorMessage('');
    } catch (error: any) {
      console.error('Error deleting category:', error);
      setSuccessMessage('');
      setErrorMessage(error?.message || 'Failed to delete category');
    }
  };

  const handleSavePortfolioIntro = async () => {
    try {
      setSavingIntro(true);
      const requestedLayout = portfolioIntro.layout_style;
      const response = await sectionApi.update('portfolio', {
        heading: portfolioIntro.heading,
        description: portfolioIntro.description,
        portfolio_page_heading: portfolioIntro.portfolio_page_heading,
        portfolio_page_description: portfolioIntro.portfolio_page_description,
        layout_style: requestedLayout
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Save failed');
      }
      await loadPortfolioIntro();
      window.dispatchEvent(new Event('projects:updated'));
      setErrorMessage('');
      setSuccessMessage(`Portfolio settings saved successfully (${requestedLayout}).`);
    } catch (error: any) {
      console.error('Error saving portfolio heading settings:', error);
      setSuccessMessage('');
      setErrorMessage(`Failed to save portfolio settings: ${error?.message || 'Please try again.'}`);
    } finally {
      setSavingIntro(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      console.log('🔧 Submitting portfolio item:', { editingItem, formData });
      console.log('🔑 Token available:', !!localStorage.getItem('token'));
      
      if (editingItem) {
        // Update existing item - only send defined values
        const updateData: any = {};
        
        if (formData.title !== undefined && formData.title !== '') updateData.title = formData.title;
        if (formData.description !== undefined && formData.description !== '') updateData.description = formData.description;
        if (formData.category !== undefined && formData.category !== '') updateData.category = formData.category;
        if (formData.image_url !== undefined && formData.image_url !== '') updateData.image_url = formData.image_url;
        if (formData.display_order !== undefined) updateData.display_order = formData.display_order;
        if (formData.is_active !== undefined) updateData.is_active = formData.is_active;
        if (formData.tags !== undefined) updateData.tags = formData.tags;
        if (formData.gallery !== undefined) updateData.gallery = formData.gallery;
        if (formData.disable_client) {
          updateData.client = null;
        } else if (formData.client !== undefined && formData.client !== '') {
          updateData.client = formData.client;
        }
        if (formData.disable_project_date) {
          updateData.project_date = null;
        } else if (formData.project_date !== undefined && formData.project_date !== '') {
          updateData.project_date = toDateInputValue(formData.project_date);
        }
        if (formData.disable_live_demo_url) {
          updateData.live_demo_url = null;
        } else if (formData.live_demo_url !== undefined && formData.live_demo_url !== '') {
          updateData.live_demo_url = formData.live_demo_url;
        }
        
        console.log('� Form Data:', formData);
        console.log('🔑 Token available:', !!localStorage.getItem('token'));
        console.log('�📝 Updating item with data:', updateData);
        console.log('📋 Item ID:', editingItem.id);
        
        try {
          const response = await portfolioApi.update(editingItem.id, updateData);
          console.log('✅ Update API Response:', response);
          console.log('✅ Response success:', response.data?.success);
          console.log('✅ Response message:', response.data?.message);
        } catch (apiError) {
          console.error('❌ API Error Details:', apiError);
          console.error('❌ Error Status:', apiError.response?.status);
          console.error('❌ Error Data:', apiError.response?.data);
          console.error('❌ Error Message:', apiError.message);
          throw apiError;
        }
      } else {
        // Create new item
        const createData = {
          title: formData.title || '',
          description: formData.description || '',
          category: formData.category || '',
          image_url: formData.image_url || '',
          display_order: formData.display_order || 0,
          is_active: formData.is_active ?? true,
          tags: formData.tags || [],
          gallery: formData.gallery || [],
          client: formData.disable_client ? null : (formData.client || ''),
          project_date: formData.disable_project_date ? null : toDateInputValue(formData.project_date || ''),
          live_demo_url: formData.disable_live_demo_url ? null : (formData.live_demo_url || '')
        };
        
        console.log('➕ Creating new item with data:', createData);
        const response = await portfolioApi.create(createData);
        console.log('✅ Create API Response:', response);
      }
      
      // Reset form and reload
      setShowForm(false);
      setEditingItem(null);
      setFormData({
        title: '',
        description: '',
        category: '',
        image_url: '',
        display_order: 0,
        is_active: true,
        tags: [],
        client: '',
        project_date: '',
        live_demo_url: '',
        gallery: [],
        disable_client: false,
        disable_project_date: false,
        disable_live_demo_url: false
      });
      loadPortfolioItems();
      window.dispatchEvent(new CustomEvent('portfolio:updated'));
      
      setErrorMessage('');
      setSuccessMessage(editingItem ? 'Portfolio item updated successfully!' : 'Portfolio item created successfully!');
    } catch (error) {
      console.error('❌ Error saving portfolio item:', error);
      setSuccessMessage('');
      setErrorMessage(`Error saving portfolio item: ${(error as Error).message || 'Unknown error'}`);
    }
  };

  const handleEdit = (item: PortfolioItem) => {
    console.log('📝 Editing portfolio item:', item);
    console.log('🔑 Token available:', !!localStorage.getItem('token'));
    
    setEditingItem(item);
    
    // Ensure all fields are properly set
    const editFormData = {
      title: item.title || '',
      description: item.description || '',
      category: item.category || '',
      image_url: item.image_url || '',
      display_order: item.display_order || 0,
      is_active: item.is_active ?? true,
      tags: item.tags || [],
      client: item.client || '',
      project_date: toDateInputValue(item.project_date),
      live_demo_url: item.live_demo_url || '',
      gallery: Array.isArray(item.gallery) ? item.gallery : [],
      disable_client: !item.client,
      disable_project_date: !item.project_date,
      disable_live_demo_url: !item.live_demo_url
    };
    
    console.log('📋 Setting form data:', editFormData);
    setFormData(editFormData);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this portfolio item?')) {
      try {
        await portfolioApi.delete(id);
        loadPortfolioItems();
        window.dispatchEvent(new CustomEvent('portfolio:updated'));
      } catch (error) {
        console.error('Error deleting portfolio item:', error);
      }
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      await portfolioApi.update(id, { is_active: !isActive });
      loadPortfolioItems();
      window.dispatchEvent(new CustomEvent('portfolio:updated'));
    } catch (error) {
      console.error('Error toggling active status:', error);
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      const response = editingItem
        ? await portfolioApi.uploadImage(editingItem.id, file)
        : await portfolioApi.uploadTempImage(file);

      if (response.success) {
        const uploadedUrl =
          response?.data?.image_url ||
          response?.image_url ||
          '';

        if (uploadedUrl) {
          setFormData((prev) => ({ ...prev, image_url: uploadedUrl }));
          setEditingItem((prev) => (prev ? { ...prev, image_url: uploadedUrl } : prev));
        }
        loadPortfolioItems();
        window.dispatchEvent(new CustomEvent('portfolio:updated'));
        setErrorMessage('');
        setSuccessMessage('Image uploaded successfully!');
      } else {
        setSuccessMessage('');
        setErrorMessage(response.message || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setSuccessMessage('');
      setErrorMessage('Error uploading image');
    }
  };

  const handleGalleryUpload = async (files: File[]) => {
    try {
      const response = editingItem
        ? await portfolioApi.uploadGallery(editingItem.id, files)
        : await portfolioApi.uploadTempGallery(files);

      if (response.success) {
        const returnedGallery = Array.isArray(response.data) ? response.data : [];
        setFormData((prev) => {
          const nextGallery = editingItem
            ? returnedGallery
            : Array.from(new Set([...(prev.gallery || []), ...returnedGallery]));
          return { ...prev, gallery: nextGallery };
        });
        setEditingItem((prev) => (prev ? { ...prev, gallery: returnedGallery } : prev));
        loadPortfolioItems();
        window.dispatchEvent(new CustomEvent('portfolio:updated'));
        setErrorMessage('');
        setSuccessMessage('Gallery images uploaded successfully!');
      } else {
        setSuccessMessage('');
        setErrorMessage(response.message || 'Failed to upload gallery images');
      }
    } catch (error) {
      console.error('Error uploading gallery images:', error);
      setSuccessMessage('');
      setErrorMessage('Error uploading gallery images');
    }
  };

  return (
    <div className="p-6 space-y-6 bg-black min-h-screen">
      <div className="fixed top-6 right-6 z-50 w-full max-w-sm space-y-2 pointer-events-none">
        {successMessage ? (
          <div className="pointer-events-auto rounded-lg border border-green-500/40 bg-green-500/20 px-4 py-3 text-sm text-green-100 shadow-lg">
            {successMessage}
          </div>
        ) : null}
        {errorMessage ? (
          <div className="pointer-events-auto rounded-lg border border-red-500/40 bg-red-500/20 px-4 py-3 text-sm text-red-100 shadow-lg">
            {errorMessage}
          </div>
        ) : null}
      </div>
      {/* Header */}
      <div className="flex justify-between items-center border-b border-white/10 pb-6">
        <div>
          <h1 className="text-4xl font-black text-white mb-2 tracking-tighter">
            Portfolio <span className="text-[#FF6B35]">Management</span>
          </h1>
          <p className="text-gray-400">Manage your portfolio projects and showcase</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            setFormData({
              title: '',
              description: '',
              category: '',
              image_url: '',
              display_order: 0,
              is_active: true,
              tags: [],
              client: '',
              project_date: '',
              live_demo_url: '',
              gallery: [],
              disable_client: false,
              disable_project_date: false,
              disable_live_demo_url: false
            });
            setEditingItem(null);
            setShowForm(true);
          }}
          className="px-6 py-3 bg-[#FF6B35] text-white font-black uppercase tracking-widest text-sm rounded-full shadow-[0_20px_60px_rgba(255,107,53,0.3)] hover:shadow-[0_20px_80px_rgba(255,107,53,0.5)] transition-all flex items-center gap-2"
        >
          <Plus size={18} />
          Add Project
        </motion.button>
      </div>

      {/* Portfolio Intro Settings */}
      <div className="bg-[#0b0c15]/60 border border-white/10 rounded-2xl p-5 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white">Portfolio Heading Settings</h2>
            <p className="text-sm text-gray-400">Manage front-page portfolio heading and description.</p>
          </div>
          <button
            type="button"
            onClick={handleSavePortfolioIntro}
            disabled={savingIntro}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2 disabled:opacity-60"
          >
            <Save size={16} />
            {savingIntro ? 'Saving...' : 'Save Heading'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Heading</label>
            <input
              type="text"
              value={portfolioIntro.heading}
              onChange={(e) => setPortfolioIntro({ ...portfolioIntro, heading: e.target.value })}
              className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
              placeholder="Portfolio heading"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Description</label>
            <input
              type="text"
              value={portfolioIntro.description}
              onChange={(e) => setPortfolioIntro({ ...portfolioIntro, description: e.target.value })}
              className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
              placeholder="Portfolio description"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Layout Style</label>
            <select
              value={portfolioIntro.layout_style}
              onChange={(e) => {
                const value = String(e.target.value);
                setPortfolioIntro({
                  ...portfolioIntro,
                  layout_style: value === 'style5' ? 'style5' : 'style4'
                });
              }}
              className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
            >
              <option value="style4">Style 4 (Depth Stack)</option>
              <option value="style5">Style 5 (Stacked Deck)</option>
            </select>
          </div>
        </div>
        <div className="mt-5 border-t border-white/10 pt-5">
          <h3 className="text-lg font-semibold text-white mb-1">Portfolio Page Content</h3>
          <p className="text-sm text-gray-400 mb-4">Manage the texts shown above the projects list on the portfolio page.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-300 mb-2">Portfolio Projects Heading</label>
              <input
                type="text"
                value={portfolioIntro.portfolio_page_heading}
                onChange={(e) => setPortfolioIntro({ ...portfolioIntro, portfolio_page_heading: e.target.value })}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                placeholder="Portfolio Projects"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-2">Portfolio Projects Description</label>
              <input
                type="text"
                value={portfolioIntro.portfolio_page_description}
                onChange={(e) => setPortfolioIntro({ ...portfolioIntro, portfolio_page_description: e.target.value })}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                placeholder="Recent work and case studies"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#0b0c15]/60 border border-white/10 rounded-2xl p-5 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white">Portfolio Categories</h2>
            <p className="text-sm text-gray-400">Create categories here. Add/Edit project form will use this dropdown.</p>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <input
            type="text"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreateCategory();
              }
            }}
            className="flex-1 bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
            placeholder="Add portfolio category"
          />
          <button
            type="button"
            onClick={handleCreateCategory}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center gap-2"
          >
            <Plus size={16} />
            Add Category
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <div key={category.id} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2">
              <span className="text-sm text-white">{category.name}</span>
              <button
                type="button"
                onClick={() => handleDeleteCategory(category.id)}
                className="text-red-400 hover:text-red-300"
                title="Delete category"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {!categories.length && <div className="text-sm text-gray-500">No categories added yet.</div>}
        </div>
      </div>

      {/* Portfolio Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-white/60">Loading portfolio items...</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {portfolioItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.1 }}
                className="relative group overflow-hidden rounded-2xl border border-[#FF6B35]/50 bg-gradient-to-br from-[#FF6B35]/10 to-purple-500/10 backdrop-blur-xl"
              >
                {/* Image */}
                <div className="aspect-video relative overflow-hidden">
                  {item.image_url ? (
                    <img 
                      src={assetUrl(item.image_url)} 
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                      <ImageIcon size={48} className="text-gray-600" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  
                  {/* Status Badge */}
                  <div className="absolute top-4 right-4">
                    <button
                      onClick={() => handleToggleActive(item.id, item.is_active)}
                      className={`p-2 rounded-full transition-colors ${
                        item.is_active 
                          ? 'bg-green-500/80 text-white' 
                          : 'bg-red-500/80 text-white'
                      }`}
                    >
                      {item.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="text-[#FF6B35] text-xs font-black uppercase tracking-wider">
                        {item.category}
                      </span>
                      <h3 className="text-xl font-black text-white mt-1">{item.title}</h3>
                    </div>
                    {Array.isArray(item.gallery) && item.gallery.length > 0 && (
                      <span className="px-2 py-1 text-[10px] rounded bg-orange-500/20 border border-orange-500/40 text-orange-300">
                        {item.gallery.length} gallery
                      </span>
                    )}
                  </div>
                  
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">{item.description}</p>
                  
                  {/* Meta Info */}
                  <div className="space-y-2 mb-4 text-xs text-gray-500">
                    {item.client && (
                      <div className="flex items-center gap-2">
                        <User size={12} />
                        <span>{item.client}</span>
                      </div>
                    )}
                    {item.project_date && (
                      <div className="flex items-center gap-2">
                        <Calendar size={12} />
                        <span>{new Date(item.project_date).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleEdit(item)}
                      className="flex-1 px-3 py-2 bg-blue-500/20 border border-blue-500/30 rounded-lg text-blue-400 hover:bg-blue-500/30 transition-colors flex items-center justify-center gap-2"
                    >
                      <Edit2 size={14} />
                      Edit
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleDelete(item.id)}
                      className="px-3 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/30 transition-colors"
                    >
                      <Trash2 size={14} />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-start justify-center z-50 p-4 pt-6 md:pt-10"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              ref={modalContentRef}
              tabIndex={-1}
              className="bg-gray-900 border border-white/10 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <h2 className="text-2xl font-black text-white">
                  {editingItem ? 'Edit Project' : 'Add New Project'}
                </h2>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditingItem(null);
                  }}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X size={20} className="text-white" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Title */}
                  <div>
                    <label className="block text-white font-medium mb-2 flex items-center gap-2">
                      <Type size={16} />
                      Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white focus:border-[#FF6B35] focus:outline-none transition-colors"
                      required
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-white font-medium mb-2 flex items-center gap-2">
                      <AlignLeft size={16} />
                      Category
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white focus:border-[#FF6B35] focus:outline-none transition-colors"
                    >
                      <option value="">Select category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.name}>{category.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Client */}
                  <div>
                    <label className="block text-white font-medium mb-2 flex items-center gap-2">
                      <User size={16} />
                      Client
                    </label>
                    <label className="mb-2 flex items-center gap-2 text-xs text-gray-300">
                      <input
                        type="checkbox"
                        checked={formData.disable_client}
                        onChange={(e) => setFormData({ ...formData, disable_client: e.target.checked, client: e.target.checked ? '' : formData.client })}
                      />
                      Disable
                    </label>
                    <input
                      type="text"
                      value={formData.client}
                      onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                      disabled={formData.disable_client}
                      className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white focus:border-[#FF6B35] focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Project Date */}
                  <div>
                    <label className="block text-white font-medium mb-2 flex items-center gap-2">
                      <Calendar size={16} />
                      Project Date
                    </label>
                    <label className="mb-2 flex items-center gap-2 text-xs text-gray-300">
                      <input
                        type="checkbox"
                        checked={formData.disable_project_date}
                        onChange={(e) => setFormData({ ...formData, disable_project_date: e.target.checked, project_date: e.target.checked ? '' : formData.project_date })}
                      />
                      Disable
                    </label>
                    <input
                      type="date"
                      value={formData.project_date}
                      onChange={(e) => setFormData({ ...formData, project_date: e.target.value })}
                      disabled={formData.disable_project_date}
                      className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white focus:border-[#FF6B35] focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Live Demo URL */}
                  <div>
                    <label className="block text-white font-medium mb-2 flex items-center gap-2">
                      <ExternalLink size={16} />
                      Live Demo URL
                    </label>
                    <label className="mb-2 flex items-center gap-2 text-xs text-gray-300">
                      <input
                        type="checkbox"
                        checked={formData.disable_live_demo_url}
                        onChange={(e) => setFormData({ ...formData, disable_live_demo_url: e.target.checked, live_demo_url: e.target.checked ? '' : formData.live_demo_url })}
                      />
                      Disable
                    </label>
                    <input
                      type="url"
                      value={formData.live_demo_url || ''}
                      onChange={(e) => setFormData({ ...formData, live_demo_url: e.target.value })}
                      disabled={formData.disable_live_demo_url}
                      className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white focus:border-[#FF6B35] focus:outline-none transition-colors"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-white font-medium mb-2 flex items-center gap-2">
                    <AlignLeft size={16} />
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white focus:border-[#FF6B35] focus:outline-none transition-colors"
                    rows={4}
                  />
                </div>

                {/* Featured and Gallery Upload */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white font-medium mb-2 flex items-center gap-2">
                      <ImageIcon size={16} />
                      Featured Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleImageUpload(file);
                        }
                      }}
                      className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#FF6B35] file:text-white hover:file:bg-[#FF6B35]/80"
                    />
                    {formData.image_url && (
                      <div className="mt-3 rounded-lg overflow-hidden border border-white/10 bg-white/5">
                        <img src={assetUrl(formData.image_url)} alt="featured-preview" className="w-full h-32 object-cover" />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-white font-medium mb-2 flex items-center gap-2">
                      <ImageIcon size={16} />
                      Gallery Images
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length > 0) {
                          handleGalleryUpload(files);
                        }
                      }}
                      className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#FF6B35] file:text-white hover:file:bg-[#FF6B35]/80"
                    />
                  </div>
                </div>

                {Array.isArray(formData.gallery) && formData.gallery.length > 0 && (
                  <div>
                    <label className="block text-white font-medium mb-2">Gallery Preview</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {formData.gallery.slice(0, 8).map((url, idx) => (
                        <div key={`${url}-${idx}`} className="rounded-lg overflow-hidden border border-white/10 bg-white/5">
                          <img src={assetUrl(url)} alt={`gallery-${idx + 1}`} className="w-full h-24 object-cover" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex justify-end gap-4 pt-6 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingItem(null);
                    }}
                    className="px-6 py-3 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors"
                  >
                    Cancel
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="submit"
                    className="px-6 py-3 bg-[#FF6B35] text-white font-black uppercase tracking-widest text-sm rounded-full shadow-[0_20px_60px_rgba(255,107,53,0.3)] hover:shadow-[0_20px_80px_rgba(255,107,53,0.5)] transition-all flex items-center gap-2"
                  >
                    <Save size={16} />
                    {editingItem ? 'Update' : 'Create'} Project
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ModernPortfolioEditor;
